import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { RadioSelector } from '@/components/RadioSelector';
import { ScrollableScreen } from '@/components/ScrollableScreen';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { FastestTag } from '@/feature/deposit/components/FastestTag';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { getAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useIsFirstScreenInStack } from '@/hooks/use-is-first-screen-in-stack';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { useUserStore } from '@/hooks/use-user-store';
import { KycStackNavigationProp } from '@/navigation/kyc/types';
import { exitKycToLogin, navigateBasedOnKYCCount } from '@/navigation/navigation';
import { logger } from '@/utils/logging';

import { TrustlyBrandingLogos } from '../components/TrustlyBrandingLogos';
import { KycProvider, kycRouteNames } from '../const';

export const KycSelector = () => {
    const [selectedOption, setSelectedOption] = useState<KycProvider | null>(null);
    const { t } = useTranslation('kyc');
    const tSocialWallet = useSocialWalletTranslation('kyc');
    const navigation = useNavigation<KycStackNavigationProp<'KycSelector'>>();
    const actions = useUserStore(state => state.actions);
    const isFirstScreenInStack = useIsFirstScreenInStack();

    // Track the amount of times the user accesses the KYC selector screen
    useEffect(() => {
        BetrAnalytics.trackEvent(AnalyticsEvent.KYC_SELECTOR_ACCESSED);
    }, []);

    const handleTrustlySelection = () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.KYC_SELECTED_TRUSTLY);
        setSelectedOption(KycProvider.TRUSTLY);
        navigation.navigate(kycRouteNames.ACKNOWLEDGEMENTS, { provider: KycProvider.TRUSTLY });
    };

    const handleIdcomplySelection = async () => {
        try {
            BetrAnalytics.trackEvent(AnalyticsEvent.KYC_SELECTED_IDCOMPLY);
            setSelectedOption(KycProvider.IDCOMPLY);
            const userConfig = await getAuthUserConfig();
            const failedKycCount = userConfig.data.config.FAILED_KYC_COUNT || 0;
            navigateBasedOnKYCCount(navigation, failedKycCount);
        } catch (error) {
            logger.error('Unable to get user auth config when attempting KYC via Trustly');
        }
    };

    const logoutAndExitToLogin = () => {
        actions.logout();
        exitKycToLogin(navigation);
    };
    const TrustlySelectorTitle = (
        <Row alignItems="center" gap="s8">
            <Text variant="titleLarge" color={ds.colorToken.text.primary}>
                {tSocialWallet('trustly_kyc_option_title')}
            </Text>
            <FastestTag />
        </Row>
    );

    return (
        <ScrollableScreen
            screenNavBarProps={{
                title: t('verify_identity'),
                closeIconMode: isFirstScreenInStack ? 'close' : 'back',
                onClose: logoutAndExitToLogin,
            }}
            scrollViewProps={{ style: { paddingHorizontal: 0 } }}
        >
            <MaxWidthWrapper paddingHorizontal="s16">
                <Box>
                    <Text variant="headlineMedium" mb="s6">
                        {t('choose_a_verification_option')}
                    </Text>
                    <Text color={ds.colorToken.text.secondary}>{tSocialWallet('selector_screen_description')}</Text>
                    <SizedBox value={24} />
                    <RadioSelector
                        isSelected={selectedOption === KycProvider.TRUSTLY}
                        title={TrustlySelectorTitle}
                        description={t('trustly_kyc_option_description')}
                        testID="trustly-selector"
                        onPress={handleTrustlySelection}
                        footer={<TrustlyBrandingLogos />}
                    />
                    <SizedBox value={16} />
                    <RadioSelector
                        isSelected={selectedOption === KycProvider.IDCOMPLY}
                        title={t('idcomply_kyc_option_title')}
                        description={t('idcomply_kyc_option_description')}
                        testID="idcomply-selector"
                        onPress={handleIdcomplySelection}
                    />
                </Box>
            </MaxWidthWrapper>
        </ScrollableScreen>
    );
};
